import { Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

interface PrimaryTypographyProps extends Omit<TypographyProps, 'variant'> {
  type?:
    | 'title'
    | 'subtitle'
    | 'minor-title'
    | 'heading'
    | 'subheading'
    | 'minor-heading'
    | 'body'
    | 'caption'
    | 'overline';
  weight?: 'Bold' | 'Medium' | 'Regular' | 'Light';
  dark?: boolean;
}

/**
 * Reusable typography component with predefined variants.
 * @param {'title' | 'subtitle' | 'minor-title' | 'heading' | 'subheading' |
 * 'minor-heading' | 'body' | 'caption' | 'overline'} type Optionally define the type of text to be used, defaults to body.
 * @param {'bold' | 'regular' | 'light'} weight Optionally set non-default font weight for typography type.
 * @param {boolean} dark Optionally render text for dark background as stronger contrasting color.
 * @returns {JSX.Element}
 */
const PrimaryTypography = styled(Typography, {
  shouldForwardProp: (prop: string) => prop !== 'dark' && prop !== 'type' && prop !== 'weight'
})<PrimaryTypographyProps>(({ theme, type = 'body', weight = 'Regular', dark }) => {
  return {
    fontWeight: theme.typography[`fontWeight${weight}`],
    ...(type !== 'caption' &&
      type !== 'body' && {
        color: dark ? theme.palette.primary.contrastText : theme.palette.primary.main
      }),
    ...(() => {
      switch (type) {
        case 'title':
          return {
            fontSize: theme.typography.h1.fontSize
          };
        case 'subtitle':
          return {
            fontSize: theme.typography.h2.fontSize
          };
        case 'minor-title':
          return {
            fontSize: theme.typography.h3.fontSize
          };
        case 'heading':
          return {
            fontSize: theme.typography.h4.fontSize
          };
        case 'subheading':
          return {
            fontSize: theme.typography.h5.fontSize
          };
        case 'minor-heading':
          return {
            fontSize: theme.typography.h6.fontSize
          };
        case 'body':
          return {
            fontSize: theme.typography.body1.fontSize,
            fontWeight: weight
              ? theme.typography[`fontWeight${weight}`]
              : theme.typography.fontWeightRegular
          };
        case 'caption':
          return {
            fontSize: theme.typography.caption.fontSize,
            color: dark ? theme.palette.secondary.main : theme.palette.secondary.dark
          };
        case 'overline':
          return {
            fontSize: theme.typography.overline.fontSize,
            color: dark ? theme.palette.secondary.main : theme.palette.secondary.dark
          };
        default:
          return {};
      }
    })()
  };
});

export const Title = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="title" {...props} />
);

export const Subtitle = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="subtitle" {...props} />
);

export const MinorTitle = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="minor-title" {...props} />
);

export const Heading = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="heading" {...props} />
);

export const Subheading = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="subheading" {...props} />
);

export const MinorHeading = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="minor-heading" {...props} />
);

export const BodyText = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="body" {...props} />
);

export const CaptionText = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="caption" {...props} />
);

export const OverlineText = (props: PrimaryTypographyProps) => (
  <PrimaryTypography type="overline" {...props} />
);

export default PrimaryTypography;
